import { catchError, map, Observable, of, tap, throwError } from 'rxjs';
import { Campaign } from '../models/campaign'
import { Injectable } from '@angular/core';
import { environment } from './../../environments/environment';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { CampaignTask } from '../models/campaignTask';
import { Deliverable } from '../models/deliverable';
import { Exclusivity } from '../models/exclusivity';

// Filters for the cross-campaign tasks query (GET tasks). All optional.
export type TaskFilter = {
  complete?: boolean;
  dueOnOrBefore?: string;        // yyyy-MM-dd
  paymentRelated?: boolean;
  creatorFacing?: boolean;
  descriptionContains?: string;
};

@Injectable({ providedIn: 'root' })
export class CampaignService {

  campaignsCache: Campaign[] = [];

  constructor(private http: HttpClient) {}

  getCampaign(id: string): Observable<Campaign> {
    var cacheIndex = this.campaignsCache.findIndex(c => c.id === id);
    if (cacheIndex >= 0) {
      return of(this.campaignsCache[cacheIndex]);
    }

    // If cache is empty, fetch all campaigns first to populate the cache
    // This ensures the cache is fully populated even when refreshing on a single campaign page
    return this.getCampaigns().pipe(
      map(campaigns => {
        const campaign = campaigns.find(c => c.id === id);
        if (!campaign) {
          throw new Error(`Campaign with id ${id} not found`);
        }
        return campaign;
      })
    );
  }

  // The list is loaded lean from campaigns/summary: tasks / deliverables / exclusivities live in
  // their own containers and are NOT part of it. The arrays on each cached campaign start empty
  // and are filled on demand by loadCampaignTasks / loadCampaignDeliverables /
  // loadCampaignExclusivities (one campaign) or loadTasks (cross-campaign, e.g. overdue).
  getCampaigns(forceRefresh = false): Observable<Campaign[]> {
    if (!forceRefresh && this.campaignsCache.length > 0) {
      return of(this.campaignsCache);
    }

    const url = `${environment.apiUrl}campaigns/summary`;
    return this.http.get<Campaign[]>(url).pipe(
      tap((result) => {
        result.forEach(c => {
          c.tasks = c.tasks ?? [];
          c.deliverables = c.deliverables ?? [];
          c.exclusivities = c.exclusivities ?? [];
        });
        this.campaignsCache = result;
        // The cached due tasks were merged into the previous campaign objects
        this.dueTasksLoadedAt = null;
      })
    );
  }

  // Every property of one campaign (GET campaigns/{id}/detail): the list only carries the few
  // fields list screens need. The result is merged onto the cached campaign object (which the
  // campaign screen binds to and edits), and that object is returned.
  //
  // Intentionally NOT GET campaigns/{id}: that route is the legacy shared contract (mobile apps)
  // and returns tasks / deliverables / exclusivities too, which this screen loads lazily per tab.
  // /detail returns the campaign's own fields only.
  loadCampaignDetail(campaignId: string): Observable<Campaign> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/detail`;
    return this.http.get<Campaign>(url).pipe(
      map((detail) => {
        let campaign = this.campaignsCache.find(c => c.id === campaignId);
        if (!campaign) {
          campaign = { ...detail, tasks: [], deliverables: [], exclusivities: [] } as Campaign;
          this.campaignsCache.push(campaign);
        } else {
          Object.assign(campaign, detail);
        }
        return campaign;
      })
    );
  }

  // The campaign screen calls these when the user opens the matching tab. They always fetch (one
  // single-partition query) and put the result on the cached campaign, so the existing cache
  // mutators (patch / add / delete) keep working on it.
  loadCampaignTasks(campaignId: string): Observable<CampaignTask[]> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/tasks`;
    return this.http.get<CampaignTask[]>(url).pipe(
      tap((tasks) => {
        const campaign = this.campaignsCache.find(c => c.id === campaignId);
        if (campaign) campaign.tasks = tasks;
      })
    );
  }

  // One campaign's own fields (GET campaigns/{id}/detail) with no side effects: unlike
  // loadCampaignDetail it does not merge onto the cached campaign. Used by the Changes tab viewer.
  getCampaignDetail(campaignId: string): Observable<Campaign> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/detail`;
    return this.http.get<Campaign>(url);
  }

  // Single exclusivity, straight from the server (does not touch the campaign cache).
  getCampaignExclusivity(campaignId: string, exclusivityId: string): Observable<Exclusivity> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/exclusivities/${exclusivityId}`;
    return this.http.get<Exclusivity>(url);
  }

  // Single task, straight from the server (does not touch the campaign cache). Used by the
  // Changes tab, where the Tasks tab may never have been opened.
  getCampaignTask(campaignId: string, taskId: string): Observable<CampaignTask> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/tasks/${taskId}`;
    return this.http.get<CampaignTask>(url);
  }

  // Single deliverable, straight from the server (does not touch the campaign cache).
  getCampaignDeliverable(campaignId: string, deliverableId: string): Observable<Deliverable> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/deliverables/${deliverableId}`;
    return this.http.get<Deliverable>(url);
  }

  loadCampaignDeliverables(campaignId: string): Observable<Deliverable[]> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/deliverables`;
    return this.http.get<Deliverable[]>(url).pipe(
      tap((deliverables) => {
        const campaign = this.campaignsCache.find(c => c.id === campaignId);
        if (campaign) campaign.deliverables = deliverables;
      })
    );
  }

  loadCampaignExclusivities(campaignId: string): Observable<Exclusivity[]> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/exclusivities`;
    return this.http.get<Exclusivity[]>(url).pipe(
      tap((exclusivities) => {
        const campaign = this.campaignsCache.find(c => c.id === campaignId);
        if (campaign) campaign.exclusivities = exclusivities;
      })
    );
  }

  // Cross-campaign task query for list screens (e.g. incomplete tasks due on or before today).
  // Returned tasks are merged into the cached campaigns' tasks by id (added or updated, never
  // removed) so those screens keep reading campaign.tasks and see local edits live. Every
  // consumer filters by its own criteria, so extra tasks already in the cache are harmless.
  // Call after getCampaigns().
  loadTasks(filter: TaskFilter): Observable<void> {
    let params = new HttpParams();
    if (filter.complete !== undefined) params = params.set('complete', String(filter.complete));
    if (filter.dueOnOrBefore) params = params.set('dueOnOrBefore', filter.dueOnOrBefore);
    if (filter.paymentRelated !== undefined) params = params.set('paymentRelated', String(filter.paymentRelated));
    if (filter.creatorFacing !== undefined) params = params.set('creatorFacing', String(filter.creatorFacing));
    if (filter.descriptionContains) params = params.set('descriptionContains', filter.descriptionContains);

    const url = `${environment.apiUrl}tasks`;
    return this.http.get<(CampaignTask & { campaignId: string })[]>(url, { params }).pipe(
      tap((tasks) => this.mergeTasksIntoCache(tasks)),
      map(() => void 0)
    );
  }

  // Every incomplete task that is past due or due today, across all campaigns (GET tasks/due).
  // Feeds the dashboard's Tasks table. Merged into the cached campaigns like loadTasks, so call
  // it after getCampaigns() has populated the cache.
  //
  // The result is cached: returning to the dashboard (e.g. after viewing a campaign) does not call
  // the server again. That is safe because task edits made in this app update the cached
  // campaigns' tasks directly (patch / add / delete), so the table stays correct. The call is
  // repeated when the cache is older than DUE_TASKS_MAX_AGE_MS (to pick up changes made by other
  // people / n8n), when the date has rolled over (tasks that became due), or on forceRefresh.
  loadDueTasks(forceRefresh = false): Observable<void> {
    if (!forceRefresh && this.dueTasksCacheIsFresh()) {
      return of(void 0);
    }

    const url = `${environment.apiUrl}tasks/due`;
    return this.http.get<(CampaignTask & { campaignId: string })[]>(url).pipe(
      tap((tasks) => {
        this.mergeTasksIntoCache(tasks);
        this.dueTasksLoadedAt = Date.now();
        this.dueTasksLoadedForDate = this.todayUtc();
      }),
      map(() => void 0)
    );
  }

  private static readonly DUE_TASKS_MAX_AGE_MS = 5 * 60 * 1000;
  private dueTasksLoadedAt: number | null = null;
  private dueTasksLoadedForDate: string | null = null;

  private todayUtc(): string {
    return new Date().toISOString().split('T')[0];
  }

  private dueTasksCacheIsFresh(): boolean {
    return this.dueTasksLoadedAt !== null
      && this.dueTasksLoadedForDate === this.todayUtc()
      && Date.now() - this.dueTasksLoadedAt < CampaignService.DUE_TASKS_MAX_AGE_MS;
  }

  private mergeTasksIntoCache(tasks: (CampaignTask & { campaignId: string })[]): void {
    const byCampaign = new Map<string, CampaignTask[]>();
    tasks.forEach(t => {
      const { campaignId, ...task } = t;
      const list = byCampaign.get(campaignId) ?? [];
      list.push(task as CampaignTask);
      byCampaign.set(campaignId, list);
    });

    this.campaignsCache.forEach(c => {
      (byCampaign.get(c.id) ?? []).forEach(loaded => {
        const index = c.tasks.findIndex(t => t.id === loaded.id);
        if (index === -1) {
          c.tasks.push(loaded);
        } else {
          c.tasks[index] = loaded;
        }
      });
    });
  }

  updateCampaignTask(updatedTask: CampaignTask): Campaign | null {
    // Caller must ensure campaign is saved.
    const campaign = this.campaignsCache.find(c => c.id === updatedTask.campaignId);
    if (!campaign) return null;

    const taskIndex = campaign.tasks.findIndex(t => t.id === updatedTask.id);
    if (taskIndex === -1) return null;

    const cleanTask = {
      ...updatedTask,
      campaignId: undefined,
      campaignName: undefined
    };

    campaign.tasks[taskIndex] = cleanTask as CampaignTask;
    return campaign;
  }

  deleteCampaignTask(task: CampaignTask): Campaign | null {
    // Caller must ensure campaign is saved.
    const campaign = this.campaignsCache.find(c => c.id === task.campaignId);
    if (!campaign) return null;

    const taskIndex = campaign.tasks.findIndex(t => t.id === task.id);
    if (taskIndex === -1) return null;

    campaign.tasks.splice(taskIndex, 1);
    return campaign;
  }

  patchCampaignTask(campaignId: string, taskId: string, fieldName: string, newValue: any): Observable<void> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/tasks/${taskId}`;
    const body = {
      fieldName: fieldName,
      newValue: newValue
    };

    return this.http.patch<void>(url, body).pipe(
      tap(() => {
        // Update the task in the local cache
        const campaign = this.campaignsCache.find(c => c.id === campaignId);
        if (campaign) {
          const taskIndex = campaign.tasks.findIndex(t => t.id === taskId);
          if (taskIndex !== -1) {
            // Update only the specific field in the cached task
            (campaign.tasks[taskIndex] as any)[fieldName] = newValue;
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to patch campaign task', error);
        return throwError(() => error);
      })
    );
  }

  AddCampaignTask(campaignId: string, task: CampaignTask): Observable<void> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/tasks`;

    return this.http.post<void>(url, task).pipe(
      tap(() => {
        // Add the new task to the local cache
        const campaign = this.campaignsCache.find(c => c.id === campaignId);
        if (campaign) {
          campaign.tasks.push(task);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to add campaign task', error);
        return throwError(() => error);
      })
    );
  }

  DeleteTask(campaignId: string, taskId: string): Observable<void> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/tasks/${taskId}`;

    return this.http.delete<void>(url).pipe(
      tap(() => {
        // Remove the task from the local cache
        const campaign = this.campaignsCache.find(c => c.id === campaignId);
        if (campaign) {
          campaign.tasks = campaign.tasks.filter(t => t.id !== taskId);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to delete campaign task', error);
        return throwError(() => error);
      })
    );
  }

  AddCampaignDeliverable(campaignId: string, deliverable: any): Observable<void> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/deliverables`;

    return this.http.post<void>(url, deliverable).pipe(
      tap(() => {
        // Add the new deliverable to the local cache
        const campaign = this.campaignsCache.find(c => c.id === campaignId);
        if (campaign) {
          campaign.deliverables.push(deliverable);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to add campaign deliverable', error);
        return throwError(() => error);
      })
    );
  }

  DeleteDeliverable(campaignId: string, deliverableId: string): Observable<void> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/deliverables/${deliverableId}`;

    return this.http.delete<void>(url).pipe(
      tap(() => {
        // Remove the deliverable from the local cache
        const campaign = this.campaignsCache.find(c => c.id === campaignId);
        if (campaign) {
          campaign.deliverables = campaign.deliverables.filter(d => d.id !== deliverableId);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to delete campaign deliverable', error);
        return throwError(() => error);
      })
    );
  }

  patchCampaignDeliverable(campaignId: string, deliverableId: string, fieldName: string, newValue: any): Observable<void> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/deliverables/${deliverableId}`;
    const body = {
      fieldName: fieldName,
      newValue: newValue
    };

    return this.http.patch<void>(url, body).pipe(
      tap(() => {
        // Update the deliverable in the local cache
        const campaign = this.campaignsCache.find(c => c.id === campaignId);
        if (campaign) {
          const deliverableIndex = campaign.deliverables.findIndex(d => d.id === deliverableId);
          if (deliverableIndex !== -1) {
            // Update only the specific field in the cached deliverable
            (campaign.deliverables[deliverableIndex] as any)[fieldName] = newValue;
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to patch campaign deliverable', error);
        return throwError(() => error);
      })
    );
  }

  reorderDeliverables(campaignId: string, orderedIds: string[]): Observable<void> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/deliverables/reorder`;
    return this.http.put<void>(url, { orderedIds }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to reorder deliverables', error);
        return throwError(() => error);
      })
    );
  }

  addFileToDeliverable(campaignId: string, deliverableId: string, fileId: string, description: string): Observable<void> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/deliverables/${deliverableId}/files`;
    const body = {
      fileId: fileId,
      description: description
    };

    return this.http.post<void>(url, body).pipe(
      tap(() => {
        // Add the new file to the deliverable in the local cache
        const campaign = this.campaignsCache.find(c => c.id === campaignId);
        if (campaign) {
          const deliverable = campaign.deliverables.find(d => d.id === deliverableId);
          if (deliverable) {
            // Initialize files array if it doesn't exist
            if (!deliverable.files) {
              deliverable.files = [];
            }
            // Add the new file to the cached deliverable
            deliverable.files.push({ fileId, description, brandFeedback: '', approved: false });
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to add file to deliverable', error);
        return throwError(() => error);
      })
    );
  }

  patchDeliverableFile(campaignId: string, deliverableId: string, fileId: string, fieldName: string, newValue: any): Observable<void> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/deliverables/${deliverableId}/files/${fileId}`;
    const body = {
      fieldName: fieldName,
      newValue: newValue
    };

    return this.http.patch<void>(url, body).pipe(
      tap(() => {
        // Update the file in the local cache
        const campaign = this.campaignsCache.find(c => c.id === campaignId);
        if (campaign) {
          const deliverable = campaign.deliverables.find(d => d.id === deliverableId);
          if (deliverable && deliverable.files) {
            const file = deliverable.files.find(f => f.fileId === fileId);
            if (file) {
              (file as any)[fieldName] = newValue;
            }
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to patch deliverable file', error);
        return throwError(() => error);
      })
    );
  }

  addUsageToDeliverable(campaignId: string, deliverableId: string, type: string, durationInDays: number): Observable<string> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/deliverables/${deliverableId}/usage`;
    const usageId = crypto.randomUUID();
    const body = {
      id: usageId,
      type: type,
      durationInDays: durationInDays
    };

    return this.http.post<void>(url, body).pipe(
      map(() => {
        // Return the ID that was used to create the usage
        console.log('Usage added to deliverable successfully');
        return usageId;
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to add usage to deliverable', error);
        return throwError(() => error);
      })
    );
  }

  deleteUsageFromDeliverable(campaignId: string, deliverableId: string, usageId: string): Observable<void> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/deliverables/${deliverableId}/usage/${usageId}`;

    return this.http.delete<void>(url).pipe(
      tap(() => {
        // Remove the usage from the local cache
        const campaign = this.campaignsCache.find(c => c.id === campaignId);
        if (campaign) {
          const deliverable = campaign.deliverables.find(d => d.id === deliverableId);
          if (deliverable && deliverable.usage) {
            deliverable.usage = deliverable.usage.filter(u => u.id !== usageId);
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to delete usage from deliverable', error);
        return throwError(() => error);
      })
    );
  }

  patchDeliverableUsage(campaignId: string, deliverableId: string, usageId: string, fieldName: string, newValue: any): Observable<void> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/deliverables/${deliverableId}/usage/${usageId}`;
    const body = {
      fieldName: fieldName,
      newValue: newValue
    };

    return this.http.patch<void>(url, body).pipe(
      tap(() => {
        // Update the usage in the local cache
        const campaign = this.campaignsCache.find(c => c.id === campaignId);
        if (campaign) {
          const deliverable = campaign.deliverables.find(d => d.id === deliverableId);
          if (deliverable && deliverable.usage) {
            const usage = deliverable.usage.find(u => u.id === usageId);
            if (usage) {
              (usage as any)[fieldName] = newValue;
            }
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to patch deliverable usage', error);
        return throwError(() => error);
      })
    );
  }

  patchCampaignExclusivity(campaignId: string, exclusivityId: string, fieldName: string, newValue: any): Observable<void> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/exclusivities/${exclusivityId}`;
    const body = {
      fieldName: fieldName,
      newValue: newValue
    };

    return this.http.patch<void>(url, body).pipe(
      tap(() => {
        // Update the exclusivity in the local cache
        const campaign = this.campaignsCache.find(c => c.id === campaignId);
        if (campaign) {
          const exclusivityIndex = campaign.exclusivities.findIndex(e => e.id === exclusivityId);
          if (exclusivityIndex !== -1) {
            // Update only the specific field in the cached exclusivity
            (campaign.exclusivities[exclusivityIndex] as any)[fieldName] = newValue;
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to patch campaign exclusivity', error);
        return throwError(() => error);
      })
    );
  }

  AddCampaignExclusivity(campaignId: string, exclusivity: any): Observable<void> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/exclusivities`;

    return this.http.post<void>(url, exclusivity).pipe(
      tap(() => {
        // Add the new exclusivity to the local cache
        const campaign = this.campaignsCache.find(c => c.id === campaignId);
        if (campaign) {
          campaign.exclusivities.push(exclusivity);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to add campaign exclusivity', error);
        return throwError(() => error);
      })
    );
  }

  DeleteExclusivity(campaignId: string, exclusivityId: string): Observable<void> {
    const url = `${environment.apiUrl}campaigns/${campaignId}/exclusivities/${exclusivityId}`;

    return this.http.delete<void>(url).pipe(
      tap(() => {
        // Remove the exclusivity from the local cache
        const campaign = this.campaignsCache.find(c => c.id === campaignId);
        if (campaign) {
          campaign.exclusivities = campaign.exclusivities.filter(e => e.id !== exclusivityId);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to delete campaign exclusivity', error);
        return throwError(() => error);
      })
    );
  }

  patchCampaignField(campaignId: string, fieldName: string, newValue: any): Observable<void> {
    const url = `${environment.apiUrl}campaigns/${campaignId}`;
    const body = {
      fieldName: fieldName,
      newValue: newValue
    };

    return this.http.patch<void>(url, body).pipe(
      tap(() => {
        // Update the campaign in the local cache since server returned 200
        const campaignIndex = this.campaignsCache.findIndex(c => c.id === campaignId);
        if (campaignIndex !== -1) {
          // Update only the specific field in the cached campaign
          (this.campaignsCache[campaignIndex] as any)[fieldName] = newValue;
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Failed to patch campaign field', error);
        return throwError(() => error);
      })
    );
  }

  addNewCampaign(rosterId?: string, source?: string, rate?: number): Observable<Campaign> {
    const url = `${environment.apiUrl}campaigns`;
    const body: any = {};
    if (rosterId) {
      body.creatorId = rosterId;
    }
    if (source) {
      body.source = source;
    }
    if (rate !== undefined) {
      body.rate = rate;
    }
    return this.http.post<Campaign>(url, Object.keys(body).length > 0 ? body : null).pipe(
      tap((campaign) => {
        // Same shape as list entries: child arrays exist and start empty
        campaign.tasks = campaign.tasks ?? [];
        campaign.deliverables = campaign.deliverables ?? [];
        campaign.exclusivities = campaign.exclusivities ?? [];
      })
    );
  }

  private getRandomDateInLastYear(): Date {
    const now = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(now.getFullYear() - 1);

    // Generate a random timestamp between one year ago and now
    const randomTimestamp = oneYearAgo.getTime() + Math.random() * (now.getTime() - oneYearAgo.getTime());

    // Create and return a Date object
    return new Date(randomTimestamp);
  }
}
